import {
	deleteSenderPreset,
	duplicateSenderPreset,
	fetchSenderPresets,
	type SenderRunPreset,
} from '@/api/sender-presets';
import { fetchSenderAutoCampaign, upsertSenderAutoCampaign } from '@/api/sender-auto-campaign';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { $features } from '@/entities/features-entity';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import { senderRoutes } from '@/lib/sender-routes';
import { formatScenarioMeaning } from '@/pages/sender/scenario-meaning';
import Pressable from '@/components/motion/pressable';
import SenderEmptyState from '@/pages/sender/components/sender-empty-state';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import { senderSurfaceClass } from '@/pages/sender/components/sender-surface';
import { SenderScreen, SENDER_ADMIN_DISABLED, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { cn } from '@/lib/utils';
import { ChevronRightIcon } from 'lucide-react';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export default function SenderScenariosPage() {
	const navigate = useNavigate();
	const { senderPresetsEnabled } = useUnit($features);
	const [presets, setPresets] = useState<SenderRunPreset[]>([]);
	const [autoIds, setAutoIds] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [query, setQuery] = useState('');
	const [deleteTarget, setDeleteTarget] = useState<SenderRunPreset | null>(null);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		if (!senderPresetsEnabled) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(false);
		try {
			const [items, campaign] = await Promise.all([
				fetchSenderPresets(),
				fetchSenderAutoCampaign().catch(() => null),
			]);
			setPresets(items);
			setAutoIds(campaign?.presetIds ?? []);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [senderPresetsEnabled]);

	useEffect(() => {
		void load();
	}, [load]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return presets;
		return presets.filter(preset => preset.name.toLowerCase().includes(q));
	}, [presets, query]);

	async function duplicate(id: string) {
		try {
			await duplicateSenderPreset(id);
			toast.success('Сценарий скопирован');
			await load();
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось дублировать'));
		}
	}

	async function remove() {
		if (!deleteTarget) return;
		setBusy(true);
		try {
			await deleteSenderPreset(deleteTarget.id);
			if (autoIds.includes(deleteTarget.id)) {
				await upsertSenderAutoCampaign({ presetIds: autoIds.filter(id => id !== deleteTarget.id) });
			}
			toast.success('Сценарий удалён');
			setDeleteTarget(null);
			await load();
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось удалить'));
		} finally {
			setBusy(false);
		}
	}

	if (!senderPresetsEnabled) {
		return (
			<SenderScreen title='Сценарии'>
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			</SenderScreen>
		);
	}

	return (
		<SenderScreen
			title='Сценарии'
			footer={
				<Button type='button' className={senderPrimaryButtonClassName()} onClick={() => navigate(senderRoutes.scenarioNew)}>
					Создать сценарий
				</Button>
			}
		>
			{presets.length > 7 ? (
				<Input
					value={query}
					onChange={event => setQuery(event.target.value)}
					placeholder='Название'
					className='min-h-11 text-[15px]'
				/>
			) : null}
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<SenderLoadError onRetry={() => void load()} />
			) : filtered.length === 0 ? (
				<SenderEmptyState message='Сценариев нет' />
			) : (
				<ul className='flex flex-col gap-2'>
					{filtered.map(preset => (
						<li key={preset.id} className={cn(senderSurfaceClass, 'overflow-hidden')}>
							<Pressable
								role='button'
								tabIndex={0}
								className='flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2.5 text-left active:bg-accent/30'
								onClick={() => navigate(`${senderRoutes.scenarios}/${preset.id}`)}
							>
								<div className='min-w-0'>
									<span className='block text-[15px] font-medium'>{preset.name}</span>
									<span className='block text-[13px] text-muted-foreground'>{formatScenarioMeaning(preset)}</span>
								</div>
								<ChevronRightIcon size={18} className='shrink-0 text-muted-foreground' />
							</Pressable>
							<div className='flex border-t border-border/60'>
								<button
									type='button'
									className='min-h-11 flex-1 text-[15px] text-primary'
									onClick={() => void duplicate(preset.id)}
								>
									Дублировать
								</button>
								<button
									type='button'
									className='min-h-11 flex-1 text-[15px] text-destructive'
									onClick={() => setDeleteTarget(preset)}
								>
									Удалить
								</button>
							</div>
						</li>
					))}
				</ul>
			)}
			<Dialog open={Boolean(deleteTarget)} onOpenChange={open => !open && setDeleteTarget(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Удалить сценарий?</DialogTitle>
					</DialogHeader>
					{deleteTarget && autoIds.includes(deleteTarget.id) ? (
						<p className='text-[15px]'>Он будет снят с авторассылки</p>
					) : null}
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' variant='destructive' className='min-h-11' disabled={busy} onClick={() => void remove()}>
							Удалить
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setDeleteTarget(null)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SenderScreen>
	);
}
